import { useState, useEffect, useCallback } from 'react';
import { apiService, AdminUser, AdminUserData } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface UseAdminUsersReturn {
  adminUsers: AdminUser[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  fetchAdminUsers: (page?: number) => Promise<void>;
  createAdminUser: (userData: AdminUserData) => Promise<void>;
  updateAdminUser: (userId: number, userData: Partial<AdminUserData>) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useAdminUsers = (): UseAdminUsersReturn => {
  const { token } = useAuth();
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchAdminUsers = useCallback(async (page: number = 1) => {
    if (!token) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getAdminUsers(page, 10, token);
      
      if (response.success && response.users) {
        setAdminUsers(response.users);
        setCurrentPage(page);
        
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
          setTotalUsers(response.pagination.total);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch admin users');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch admin users';
      
      // Check if it's a specific backend error
      if (errorMessage.includes('Failed to fetch admin users')) {
        setError('⚠️ Admin Users endpoint is currently unavailable. This appears to be a backend issue with the /api/admin-users endpoint. Other features are working normally.');
        console.error('🚨 Backend Issue: /api/admin-users endpoint returning 500 error');
        console.error('💡 Recommendation: Check backend logs for admin users endpoint');
      } else {
        setError(errorMessage);
      }
      
      console.error('Error fetching admin users:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createAdminUser = useCallback(async (userData: AdminUserData) => {
    if (!token) throw new Error('No authentication token');

    try {
      const response = await apiService.createAdminUser(userData, token);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create admin user');
      }
      
      // Refresh the list after successful creation
      await fetchAdminUsers(currentPage);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create admin user';
      setError(errorMessage);
      throw err;
    }
  }, [token, fetchAdminUsers, currentPage]);

  const updateAdminUser = useCallback(async (userId: number, userData: Partial<AdminUserData>) => {
    if (!token) throw new Error('No authentication token');

    try {
      const response = await apiService.updateAdminUser(userId, userData, token);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update admin user');
      }
      
      // Refresh the list after successful update
      await fetchAdminUsers(currentPage);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update admin user';
      setError(errorMessage);
      throw err;
    }
  }, [token, fetchAdminUsers, currentPage]);

  const refreshData = useCallback(async () => {
    await fetchAdminUsers(currentPage);
  }, [fetchAdminUsers, currentPage]);

  // Initial load
  useEffect(() => {
    fetchAdminUsers();
  }, [fetchAdminUsers]);

  return {
    adminUsers,
    loading,
    error,
    currentPage,
    totalPages,
    totalUsers,
    fetchAdminUsers,
    createAdminUser,
    updateAdminUser,
    refreshData,
  };
};